import { Injectable, inject } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, timeout } from 'rxjs/operators';
import { DashboardApiService } from '../../shared/services/api/dashboard-api.service';
import {
  TenantOption,
  TenantSwitchContext,
  TenantSwitchOptions,
  TenantSwitchResult,
} from '../tenant/tenant-lifecycle.models';
import { TenantSelectionService } from '../../shared/components/tenant-select/tenant-select.service';
import { UiConfigService } from '../../shared/services/config/ui-config.service';
import { DateContextService } from '../../shared/services/date-context/date-context.service';
import { FilterFacade } from '../filters/filter.facade';
import { FilterLifecycleFacade } from '../filters/filter-lifecycle.facade';
import { FilterStore } from '../filters/filter.store';
import { UIConfig } from '../../shared/models/ui-config.model';
import { DataTableApiService } from '../../shared/services/api/data-table-api.service';
import { HierarchyContextService } from '../../shared/services/hierarchy-context/hierarchy-context.service';

export type { TenantSwitchOptions, TenantSwitchResult } from '../tenant/tenant-lifecycle.models';

@Injectable({ providedIn: 'root' })
export class TenantSwitchCoordinatorService {
  private static readonly DEFAULT_SWITCH_TIMEOUT_MS = 15_000;

  private dashboardApi = inject(DashboardApiService);
  private tenantSelection = inject(TenantSelectionService);
  private uiCfg = inject(UiConfigService);
  private dateCtx = inject(DateContextService);
  private filterFacade = inject(FilterFacade);
  private filterLifecycle = inject(FilterLifecycleFacade);
  private filterStore = inject(FilterStore);
  private dataTableApi = inject(DataTableApiService);
  private hierarchyCtx = inject(HierarchyContextService);
  private inFlightSwitch$?: Observable<TenantSwitchResult>;
  private inFlightTenantKey: string | null = null;

  switchTenantAndBootstrap(
    tenant: TenantOption,
    options?: TenantSwitchOptions
  ): Observable<TenantSwitchResult> {
    if (!tenant?.value) {
      return throwError(() => new Error('Tenant key is required for tenant switch'));
    }
    const prefetchFilters = options?.prefetchFilters ?? true;
    const timeoutMs =
      options?.timeoutMs ?? TenantSwitchCoordinatorService.DEFAULT_SWITCH_TIMEOUT_MS;
    if (this.inFlightSwitch$) {
      if (this.inFlightTenantKey === tenant.value) {
        return this.inFlightSwitch$;
      }
      const err = new Error(
        `Tenant switch already in progress (current: ${this.inFlightTenantKey}, requested: ${tenant.value})`
      );
      console.warn('TenantSwitchCoordinator:', err.message);
      return throwError(() => err);
    }

    const activeTenantKey = this.tenantSelection.getCurrentTenantKey();
    const switchEpoch = this.tenantSelection.startTenantSwitch(tenant.value);
    const switchContext = this.tenantSelection.getTenantSwitchContext();
    const activeSwitchContext: Pick<TenantSwitchContext, 'version' | 'switchId'> = {
      version: switchEpoch,
      switchId: switchContext.switchId,
    };
    if (activeTenantKey && activeTenantKey !== tenant.value) {
      this.filterStore.clearAllFilterOptionsCache();
    }
    this.dateCtx.setLastTransactionLoading(true);
    this.hierarchyCtx.setHierarchyLoadingFor(tenant.value, true);

    let tenantPublished = false;

    const publishTenant = (): void => {
      if (tenantPublished || !this.isSwitchContextActive(activeSwitchContext)) return;
      tenantPublished = true;
      this.tenantSelection.setTenant(tenant);
    };
    publishTenant();

    this.fetchLastTransactionDateInBackground(tenant.value, activeSwitchContext, timeoutMs);
    this.fetchHierarchyColumnsInBackground(tenant.value, activeSwitchContext, timeoutMs);

    const switch$ = this.waitForTenantConfig(tenant.value, timeoutMs).pipe(
      switchMap((config) => {
        if (!this.isSwitchContextActive(activeSwitchContext)) {
          return of({ config });
        }
        if (!config) {
          return of({ config });
        }
        return this.filterLifecycle.prepareForTenant(tenant.value, config, timeoutMs).pipe(
          map(() => {
            if (prefetchFilters) {
              this.prefetchFiltersInBackground(tenant.value, timeoutMs);
            }
            return { config };
          })
        );
      }),
      map(({ config }) => ({
        tenant,
        switchEpoch,
        switchId: activeSwitchContext.switchId,
        config,
      })),
      finalize(() => {
        this.inFlightSwitch$ = undefined;
        this.inFlightTenantKey = null;
        this.tenantSelection.endTenantSwitch(switchEpoch);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inFlightSwitch$ = switch$;
    this.inFlightTenantKey = tenant.value;
    return switch$;
  }

  private waitForTenantConfig(tenantKey: string, timeoutMs: number): Observable<UIConfig | null> {
    return this.uiCfg.loadTenantConfig(tenantKey).pipe(
      take(1),
      timeout(timeoutMs),
      catchError((err) => {
        console.error(
          `[TenantSwitchCoordinator] Config load failed for tenant "${tenantKey}":`,
          err
        );
        return of(null);
      })
    );
  }

  private fetchLastTransactionDateInBackground(
    tenantKey: string,
    switchContext: Pick<TenantSwitchContext, 'version' | 'switchId'>,
    timeoutMs: number
  ): void {
    this.dashboardApi
      .getLastTransactionDate(tenantKey)
      .pipe(
        timeout(timeoutMs),
        catchError(() => of(null)),
        take(1)
      )
      .subscribe((resp) => {
        if (!this.isSwitchContextActive(switchContext)) return;
        this.dateCtx.storeLastTransactionDateFromApi(tenantKey, resp);
        this.dateCtx.setLastTransactionLoading(false);
        if (resp?.last_transaction_date) {
          this.filterFacade.ensureDateRangeDefaults();
        }
      });
  }

  private prefetchFiltersInBackground(tenantKey: string, timeoutMs: number): void {
    from(this.filterFacade.prefetchAllFilterOptions(tenantKey))
      .pipe(
        timeout(timeoutMs),
        catchError(() => of(undefined)),
        take(1)
      )
      .subscribe();
  }

  private fetchHierarchyColumnsInBackground(
    tenantKey: string,
    switchContext: Pick<TenantSwitchContext, 'version' | 'switchId'>,
    timeoutMs: number
  ): void {
    this.dataTableApi
      .getHierarchyColumns(tenantKey)
      .pipe(
        timeout(timeoutMs),
        catchError(() => of(null)),
        finalize(() => {
          this.hierarchyCtx.setHierarchyLoadingFor(tenantKey, false);
        }),
        take(1)
      )
      .subscribe((resp) => {
        if (!this.isSwitchContextActive(switchContext)) return;
        this.hierarchyCtx.storeHierarchyColumnsFromApi(tenantKey, resp);
      });
  }

  private isSwitchContextActive(
    context: Pick<TenantSwitchContext, 'version' | 'switchId'>
  ): boolean {
    const current = this.tenantSelection.getTenantSwitchContext();
    return current.version === context.version && current.switchId === context.switchId;
  }
}
